// leaderboardService.js
// Real Firestore leaderboard reads and draft submission.
// Mirrors FirebaseRepository.swift submitLeaderboardEntry() and
// loadLeaderboardData() exactly — same collection, same field names,
// same document shape so web scores appear on the iOS leaderboard and vice versa.

import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
} from "firebase/firestore";
import { db } from "../firebase";

// ─── Sport name mapping ────────────────────────────────────────────────────
// Firestore sport field uses the iOS rawValue — capitalized full name.

const SPORT_RAW = {
  football:   "Football",
  baseball:   "Baseball",
  basketball: "Basketball",
  hockey:     "Hockey",
  soccer:     "Soccer",
};

const RAW_TO_ID = Object.fromEntries(Object.entries(SPORT_RAW).map(([k, v]) => [v, k]));

// ─── Pacific timezone helpers ─────────────────────────────────────────────────
// All daily/monthly leaderboard windows are anchored to MIDNIGHT PACIFIC, not the
// device's local midnight, so the web rolls over at the same instant as iOS and
// the Cloud Functions (which use these exact helpers). setHours(0,0,0,0) would be
// device-local and silently wrong for any user outside PT. Handles PST (UTC-8)
// and PDT (UTC-7) by checking which UTC hour lands on Pacific midnight.

export function startOfPacificDay(date = new Date()) {
  const ymd = date.toLocaleDateString("en-CA", { timeZone: "America/Los_Angeles" }); // "YYYY-MM-DD"
  const [y, m, d] = ymd.split("-").map(Number);
  for (const utcHour of [8, 7]) {
    const candidate = new Date(Date.UTC(y, m - 1, d, utcHour, 0, 0));
    const pacificHour = candidate
      .toLocaleTimeString("en-US", { timeZone: "America/Los_Angeles", hour: "2-digit", hour12: false })
      .slice(0, 2);
    if (pacificHour === "00") return candidate;
  }
  return new Date(Date.UTC(y, m - 1, d, 8, 0, 0)); // PST fallback
}

export function startOfPacificMonth(date = new Date()) {
  const ymd = date.toLocaleDateString("en-CA", { timeZone: "America/Los_Angeles" }); // "YYYY-MM-DD"
  const [y, m] = ymd.split("-").map(Number);
  for (const utcHour of [8, 7]) {
    const candidate = new Date(Date.UTC(y, m - 1, 1, utcHour, 0, 0));
    const pacificHour = candidate
      .toLocaleTimeString("en-US", { timeZone: "America/Los_Angeles", hour: "2-digit", hour12: false })
      .slice(0, 2);
    if (pacificHour === "00") return candidate;
  }
  return new Date(Date.UTC(y, m - 1, 1, 8, 0, 0)); // PST fallback
}

// ─── Submit draft score ────────────────────────────────────────────────────
// Matches FirebaseRepository.submitLeaderboardEntry() exactly.
// Document shape required by Firestore rules:
//   userID, username, sport, gameMode, teamRating, draftDate, data

export async function submitDraftScore({
  uid,
  username,
  sport,           // sport object { id, name, hex }
  draftedPlayers,
  draftPositions,
  teamRating,
  baseRating,
  chemistryBonus,
}) {
  const docId = crypto.randomUUID();
  const now = new Date();

  // The "data" field mirrors the JSON blob the iOS app writes —
  // it contains the full entry so it can be reconstructed on read.
  const entryData = JSON.stringify({
    id: docId,
    userID: uid,
    username,
    sport: SPORT_RAW[sport.id],
    teamRating,
    baseRating,
    chemistryBonus,
    teamQuality: getTeamQuality(teamRating),
    players: draftedPlayers,
    positions: draftPositions,
    draftDate: now.toISOString(),
    isCurrentUser: true,
  });

  await setDoc(doc(db, "leaderboard_submissions", docId), {
    userID:     uid,
    username:   username.toUpperCase(),
    sport:      SPORT_RAW[sport.id],
    gameMode:   "draft",
    teamRating,
    draftDate:  Timestamp.fromDate(now),
    data:       entryData,
  });
}

// ─── Load leaderboard snapshot ────────────────────────────────────────────
// Reads from leaderboard_snapshots — the pre-computed top-100 written by
// the rebuildLeaderboardSnapshots Cloud Function every 30 minutes.
// Falls back to a direct query if the snapshot doesn't exist yet.

export async function loadLeaderboardSnapshot({ sport, timeframe, gameMode = "draft" }) {
  const sportRaw = SPORT_RAW[sport.id];
  // Doc ID and fields must match what the rebuildLeaderboardSnapshots Cloud
  // Function writes: `${Sport}_${TIMEFRAME}_${gameMode}`, with TIMEFRAME kept
  // uppercase and hyphenated ("DAILY" | "MONTHLY" | "ALL-TIME"), and an
  // `entries` array whose score/date fields are `score` and `date`.
  const docId = `${sportRaw}_${timeframe}_${gameMode}`;

  try {
    const snap = await getDoc(doc(db, "leaderboard_snapshots", docId));
    if (snap.exists()) {
      const data = snap.data();
      // Pre-computed, already ranked top-100 from the snapshot. `score` is the
      // mode's numeric (teamRating for draft, streakCount for streak). We expose
      // it as both `score` and `teamRating` so the shared entry row renders it.
      const entries = (data.entries || []).map((e, i) => ({
        id: e.id || i,
        username: e.username || "",
        score: e.score || 0,
        teamRating: e.score || 0,
        userID: e.userID || "",
        draftDate: e.date?.toDate?.() || new Date(),
        userLevel: e.userLevel ?? null,
        isCurrentUser: false, // set by caller
      }));
      return {
        entries,
        cutoffScore: data.cutoffScore || 0,
        lastUpdated: data.lastUpdated?.toDate?.() || null,
      };
    }
  } catch (err) {
    console.warn("leaderboardService: snapshot read failed, falling back to direct query", err);
  }

  // Fallback: direct query if the snapshot doc doesn't exist yet.
  return loadLeaderboardDirect({ sport, timeframe, gameMode });
}

// ─── Direct leaderboard query (fallback) ──────────────────────────────────

async function loadLeaderboardDirect({ sport, timeframe, gameMode = "draft" }) {
  const sportRaw = SPORT_RAW[sport.id];
  const scoreField = gameMode === "draft" ? "teamRating" : "streakCount";
  const dateField  = gameMode === "draft" ? "draftDate"  : "completionDate";

  const base = query(
    collection(db, "leaderboard_submissions"),
    where("gameMode", "==", gameMode),
    where("sport", "==", sportRaw)
  );

  let q;

  if (timeframe === "DAILY") {
    q = query(base,
      where(dateField, ">=", Timestamp.fromDate(startOfPacificDay())),
      orderBy(dateField),
      limit(500)
    );
  } else if (timeframe === "MONTHLY") {
    q = query(base,
      where(dateField, ">=", Timestamp.fromDate(startOfPacificMonth())),
      orderBy(dateField),
      limit(500)
    );
  } else {
    q = query(base, orderBy(scoreField, "desc"), limit(100));
  }

  const snapshot = await getDocs(q);
  const entries = snapshot.docs.map(d => {
    const score = d.data()[scoreField] || 0;
    return {
      id: d.id,
      username: d.data().username || "",
      score,
      teamRating: score,
      userID: d.data().userID || "",
      draftDate: d.data()[dateField]?.toDate?.() || new Date(),
      isCurrentUser: false,
    };
  });

  // Sort by score descending (needed for daily/monthly which sort by date first)
  return {
    entries: entries.sort((a, b) => b.score - a.score).slice(0, 100),
    cutoffScore: 0,
    lastUpdated: null,
  };
}

// ─── Daily draft limit check ──────────────────────────────────────────────────
// Uses existing index: (gameMode ASC, userID ASC, draftDate DESC)
// Fetches the user's most recent draft for this sport, checks client-side if today.

export async function hasSubmittedDraftToday(uid, sportId) {
  const sportRaw = SPORT_RAW[sportId];
  const startOfDay = startOfPacificDay();

  const q = query(
    collection(db, "leaderboard_submissions"),
    where("gameMode", "==", "draft"),
    where("userID", "==", uid),
    orderBy("draftDate", "desc"),
    limit(5)
  );

  const snap = await getDocs(q);
  return snap.docs.some(d => {
    const draftDate = d.data().draftDate?.toDate?.();
    return d.data().sport === sportRaw && draftDate && draftDate >= startOfDay;
  });
}

// ─── Today's scores for all sports ───────────────────────────────────────────
// Uses existing index: (gameMode ASC, userID ASC, draftDate DESC)
// Fetches the user's 10 most recent draft submissions, then filters client-side
// for today — avoids needing a new composite index.

export async function getTodaysDraftScores(uid) {
  const startOfDay = startOfPacificDay();

  const q = query(
    collection(db, "leaderboard_submissions"),
    where("gameMode", "==", "draft"),
    where("userID", "==", uid),
    orderBy("draftDate", "desc"),
    limit(10)
  );

  const snap = await getDocs(q);
  const scores = { football: null, baseball: null, basketball: null, hockey: null, soccer: null };

  snap.docs.forEach(d => {
    const draftDate = d.data().draftDate?.toDate?.();
    if (!draftDate || draftDate < startOfDay) return; // filter to today client-side
    const sportRaw = d.data().sport;
    const sportId = RAW_TO_ID[sportRaw];
    const rating = d.data().teamRating;
    if (sportId && rating) {
      if (scores[sportId] === null || rating > scores[sportId]) {
        scores[sportId] = rating;
      }
    }
  });

  return scores;
}

// ─── Today's draft entries for a sport ────────────────────────────────────────
// Returns ALL of the user's drafts for this sport today, newest first, each
// reconstructed from its saved `data` blob. Used to reopen the Draft Complete
// view (most recent shown first, swipeable between the rest).
// Uses existing index: (gameMode ASC, userID ASC, draftDate DESC)

export async function getTodaysDraftEntries(uid, sportId) {
  const sportRaw = SPORT_RAW[sportId];
  const startOfDay = startOfPacificDay();

  const q = query(
    collection(db, "leaderboard_submissions"),
    where("gameMode", "==", "draft"),
    where("userID", "==", uid),
    orderBy("draftDate", "desc"),
    limit(20)
  );

  const snap = await getDocs(q);

  const entries = [];
  snap.docs.forEach(d => {
    const data = d.data();
    const draftDate = data.draftDate?.toDate?.();
    if (!draftDate || draftDate < startOfDay) return; // today only
    if (data.sport !== sportRaw) return;
    if (!data.data) return;
    try {
      const parsed = JSON.parse(data.data);
      entries.push({
        draftedPlayers: parsed.players || [],
        draftPositions: parsed.positions || [],
        draftDate,
        teamRating: data.teamRating ?? parsed.teamRating ?? 0,
      });
    } catch { /* skip malformed */ }
  });

  // Newest first (the query already orders desc; this is a safety net).
  entries.sort((a, b) => b.draftDate - a.draftDate);
  return entries;
}

// ─── Submit streak score ───────────────────────────────────────────────────
// Matches FirebaseRepository.submitStreakScore() exactly. The seven keys below
// are all required by the Firestore rules for gameMode 'streak'. The Cloud
// Function ranks these into the `${Sport}_${TIMEFRAME}_streak` snapshots using
// `streakCount` (score) and `completionDate` (date).
// Guests can't write to the leaderboard (rules require auth) — caller passes a
// real uid/username only for signed-in users.

export async function submitStreakScore({ uid, username, sport, streakCount, userLevel = null }) {
  if (!uid || uid === "guest") return;        // guests: local only
  if (!streakCount || streakCount < 1) return; // nothing to rank

  const docId = crypto.randomUUID();
  const now = new Date();
  const name = (username || "").toUpperCase();

  const entryData = JSON.stringify({
    id: docId,
    userID: uid,
    username: name,
    sport: SPORT_RAW[sport.id],
    streakCount,
    completionDate: now.toISOString(),
    isCurrentUser: true,
  });

  const fields = {
    userID:         uid,
    username:       name,
    sport:          SPORT_RAW[sport.id],
    gameMode:       "streak",
    streakCount,
    completionDate: Timestamp.fromDate(now),
    data:           entryData,
  };
  if (userLevel != null) fields.userLevel = userLevel;

  await setDoc(doc(db, "leaderboard_submissions", docId), fields);
}

// ─── Today's best streak per sport ────────────────────────────────────────────
// Mirrors getTodaysDraftScores but for streaks (streakCount / completionDate),
// anchored to midnight Pacific. Drives the Home streak card's today's-best box.
// Index: (gameMode ASC, userID ASC, completionDate DESC).

export async function getTodaysStreakBests(uid) {
  const startOfDay = startOfPacificDay();

  const q = query(
    collection(db, "leaderboard_submissions"),
    where("gameMode", "==", "streak"),
    where("userID", "==", uid),
    orderBy("completionDate", "desc"),
    limit(15)
  );

  const snap = await getDocs(q);
  const bests = { football: 0, baseball: 0, basketball: 0, hockey: 0, soccer: 0 };

  snap.docs.forEach(d => {
    const date = d.data().completionDate?.toDate?.();
    if (!date || date < startOfDay) return; // today (Pacific) only
    const sportId = RAW_TO_ID[d.data().sport];
    const count = d.data().streakCount || 0;
    if (sportId && count > bests[sportId]) bests[sportId] = count;
  });

  return bests;
}

// ─── All-time best streak for a sport ─────────────────────────────────────────
// Top streakCount this user has ever posted for the sport. Drives BEST STREAK on
// the overview. Index: (gameMode ASC, userID ASC, sport ASC, streakCount DESC).

export async function getStreakAllTimeBest(uid, sportId) {
  const sportRaw = SPORT_RAW[sportId];
  const q = query(
    collection(db, "leaderboard_submissions"),
    where("gameMode", "==", "streak"),
    where("userID", "==", uid),
    where("sport", "==", sportRaw),
    orderBy("streakCount", "desc"),
    limit(1)
  );
  const snap = await getDocs(q);
  return snap.docs[0]?.data().streakCount || 0;
}

// ─── Team quality label ────────────────────────────────────────────────────

function getTeamQuality(rating) {
  if (rating >= 950) return "LEGENDARY";
  if (rating >= 900) return "ELITE";
  if (rating >= 850) return "GREAT";
  if (rating >= 800) return "GOOD";
  if (rating >= 750) return "SOLID";
  if (rating >= 700) return "AVERAGE";
  return "NEEDS WORK";
}

// ─── Knockout all-time leaderboard ─────────────────────────────────────────────
// All-time Knockout pool wins per user for a sport, read from knockout_leaderboard
// ({sport}_{userID}). Sorted client-side — wins descending, then earliest first
// win — to match the iOS ordering. No orderBy means no composite index needed.
export async function loadKnockoutLeaderboard(sportId) {
  const sportRaw = SPORT_RAW[sportId] || sportId;
  const q = query(
    collection(db, "knockout_leaderboard"),
    where("sport", "==", sportRaw)
  );
  const snap = await getDocs(q);
  const entries = snap.docs.map((d) => {
    const data = d.data() || {};
    const fw = data.firstWinDate;
    return {
      id: d.id,
      userID: data.userID,
      username: data.username || "Unknown",
      totalWins: data.totalWins || 0,
      firstWinDate: fw && typeof fw.toDate === "function" ? fw.toDate() : null,
    };
  });
  entries.sort((a, b) => {
    if (b.totalWins !== a.totalWins) return b.totalWins - a.totalWins;
    const da = a.firstWinDate ? a.firstWinDate.getTime() : Infinity;
    const dbt = b.firstWinDate ? b.firstWinDate.getTime() : Infinity;
    return da - dbt;
  });
  return { entries };
}

// ─── Pick Up Standings (all-time) ───────────────────────────────────────────
// Mirrors FirebaseRepository.loadPickUpStandings. Reads pre-aggregated W-L records
// from pickup_standings — one doc per user per sport ("{Sport}_{uid}"), maintained
// by the onPickUpGameComplete Cloud Function via FieldValue.increment(). Single
// top-100 query ordered by wins desc; the finer tiebreaks (win% → fewest losses →
// alphabetical) are applied by the caller, matching LeaderboardView_PickUp.
export async function loadPickUpStandings(sportId) {
  const sportRaw = SPORT_RAW[sportId] || sportId;
  const q = query(
    collection(db, "pickup_standings"),
    where("sport", "==", sportRaw),
    orderBy("wins", "desc"),
    limit(100)
  );
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => {
      const data = d.data() || {};
      const wins = data.wins || 0;
      const losses = data.losses || 0;
      const games = wins + losses;
      return {
        id: data.userID || d.id,
        userID: data.userID,
        username: data.username || "Player",
        wins,
        losses,
        gamesPlayed: games,
        winPct: games > 0 ? (wins / games) * 100 : 0,
      };
    })
    .filter((e) => e.userID);
}

// ─── Daily / Monthly Champions ──────────────────────────────────────────────
// Mirrors FirebaseRepository.loadDailyWinners / loadMonthlyWinners. Reads the
// per-day / per-month #1 winners written by the finalizeRanks Cloud Function:
//   daily_winners/{YYYY-MM-DD}   → { date,  draft:{Sport:{username,score}}, streak:{…}, … }
//   monthly_winners/{YYYY-MM}    → { month, draft:{Sport:{username,score}}, streak:{…}, … }
// Both collections are public-read per the security rules, so guests can view
// them too. The caller passes the Pacific date/month KEY string (the modal owns
// the Pacific date math) — these functions only read and decode.
//
// We currently surface only the draft and streak maps (the modes live on web).
// recon / gauntlet / statAttack are written by the CF too and can be added here
// when those modes ship on web — no new reads or indexes required.

// Decode one game-mode map ({ "Football": {username, score}, … }) into a plain
// object keyed by the raw sport name. Missing/malformed entries are skipped, the
// same defensive pattern the iOS decoder uses.
function parseWinnerMap(map) {
  if (!map || typeof map !== "object") return {};
  const out = {};
  for (const [sportRaw, entry] of Object.entries(map)) {
    if (
      entry &&
      typeof entry.username === "string" &&
      typeof entry.score === "number"
    ) {
      out[sportRaw] = { username: entry.username, score: entry.score };
    }
  }
  return out;
}

// Prior-day #1 winners. `dateKey` is the Pacific "YYYY-MM-DD" document ID.
// Returns null when no document exists for that day (CF hasn't run / no plays).
export async function loadDailyWinners(dateKey) {
  const snap = await getDoc(doc(db, "daily_winners", dateKey));
  if (!snap.exists()) return null;
  const d = snap.data() || {};
  return {
    date: dateKey,
    draft: parseWinnerMap(d.draft),
    streak: parseWinnerMap(d.streak),
  };
}

// Completed-month #1 winners. `monthKey` is the Pacific "YYYY-MM" document ID.
// Returns null when no document exists for that month.
export async function loadMonthlyWinners(monthKey) {
  const snap = await getDoc(doc(db, "monthly_winners", monthKey));
  if (!snap.exists()) return null;
  const d = snap.data() || {};
  return {
    month: monthKey,
    draft: parseWinnerMap(d.draft),
    streak: parseWinnerMap(d.streak),
  };
}
